import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Chart from "react-apexcharts";
import AuthContext from "../AuthContext";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { ArrowPathIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { apiFetch } from "../api/client";
import aiAssistantAvatar from "../assets/ai-assistant-avatar.png";

ChartJS.register(ArcElement, Tooltip, Legend);

const chartPalette = ["#8A17D1", "#D326AD", "#B06BE6", "#E76BC7", "#6C2FA1", "#F29AD9"];
const chartText = "#E6EEF3";
const chartGrid = "rgba(199, 210, 254, 0.16)";

function Spinner() {
    return (
        <div className="flex items-center justify-center h-64 w-full">
            <div className="w-10 h-10 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
        </div>
    );
}

function SubscriptionReminder({ subscription }) {
    const expiry = subscription?.expiresAt || subscription?.ExpiresAt;
    if (!expiry) return null;
    const daysLeft = Math.ceil((new Date(expiry) - new Date()) / 86400000);
    return <div className="subscription-reminder-track"><div className="subscription-reminder"><span>{daysLeft <= 0 ? 'Your subscription has expired.' : `Your ${subscription.name || subscription.Name} plan renews in ${daysLeft} day${daysLeft === 1 ? '' : 's'}.`}</span></div></div>;
}

function StokrazeAiAssistant({ isPremium }) {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [answer, setAnswer] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const askQuestion = async (event) => {
        event.preventDefault();
        if (!message.trim()) return;

        setLoading(true);
        setError("");
        setAnswer("");

        try {
            const res = await apiFetch("/api/ai/chat", {
                method: "POST",
                body: JSON.stringify({ message })
            });
            const data = await res.json().catch(() => null);
            if (!res.ok || data?.succeeded === false) {
                setError(data?.message || "AI service is temporarily unavailable.");
                return;
            }
            setAnswer(data?.data || data?.message || "I could not generate an answer.");
        } catch (err) {
            console.error("Unable to ask AI assistant", err);
            setError("AI service is temporarily unavailable.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <button type="button" onClick={() => setOpen(true)} className="fixed bottom-[3.6rem] right-3 z-50 h-12 w-12 overflow-hidden rounded-full border-2 border-[#D326AD] bg-[#111B31] shadow-xl shadow-[#020617]/50 sm:bottom-5 sm:right-5 sm:h-16 sm:w-16" title="Stokraze AI Assistant">
                <img src={aiAssistantAvatar} alt="Stokraze AI assistant" className="h-full w-full object-cover" />
            </button>
            {open && (
                <section className="fixed bottom-28 right-3 z-50 w-[calc(100%-1.5rem)] max-w-md rounded-xl border border-[#8A17D1]/35 bg-[#111B31] p-5 shadow-2xl shadow-[#020617]/60 sm:bottom-24 sm:right-5 sm:w-[calc(100%-2.5rem)]">
                    <div className="flex items-center gap-3">
                        <img src={aiAssistantAvatar} alt="Stokraze AI assistant" className="h-10 w-10 rounded-full" />
                        <div>
                            <h2 className="font-semibold text-[#E6EEF3]">Stokraze AI Assistant</h2>
                            <p className="text-xs text-[#C7D2FE]">Ask about your business data</p>
                        </div>
                        <button onClick={() => setOpen(false)} className="ml-auto text-[#C7D2FE]">×</button>
                    </div>

                    {isPremium ? (
                        <form onSubmit={askQuestion} className="mt-4 space-y-3">
                            <textarea value={message} onChange={(event) => setMessage(event.target.value)} rows="3" className="w-full rounded-lg border border-[#C7D2FE]/15 bg-[#071026]/70 p-3 text-sm text-[#E6EEF3] outline-none focus:border-[#D326AD]" placeholder="Example: What sold most this week?" />
                            <button type="submit" disabled={loading || !message.trim()} className="w-full rounded-lg bg-[#8A17D1] px-4 py-2 text-sm font-semibold text-white hover:bg-[#D326AD] disabled:cursor-not-allowed disabled:opacity-60">
                                {loading ? "Asking..." : "Ask Stokraze AI"}
                            </button>
                            {answer && <div className="rounded-lg bg-[#071026]/70 p-3 text-sm leading-6 text-[#E6EEF3]">{answer}</div>}
                            {error && <div className="rounded-lg border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-100">{error}</div>}
                        </form>
                    ) : (
                        <div className="mt-4 rounded-lg bg-[#071026]/70 p-4 text-sm text-[#E6EEF3]">Upgrade to Premium to use Stokraze AI Assistant.</div>
                    )}
                </section>
            )}
        </>
    );
}

function AiInventoryAdvisor({ insights, loading, onRefresh }) {
    const urgentRestocks = insights?.urgentRestocks || insights?.data?.urgentRestocks || [];
    const summary = insights?.summary || insights?.data?.summary;

    return (
        <section className="col-span-full rounded-xl border border-[#8A17D1]/35 bg-[#111B31] p-5 shadow-lg shadow-[#020617]/30 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#8A17D1]/20 text-[#d9a7ff]">
                        <SparklesIcon className="h-6 w-6" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-lg font-semibold text-[#E6EEF3]">AI Inventory Advisor</h2>
                            <span className="rounded-full bg-[#D326AD]/20 px-2 py-0.5 text-xs font-semibold text-[#f3a1dc]">Premium</span>
                        </div>
                        <p className="mt-1 text-sm text-[#C7D2FE]">Restock recommendations and stock insights based on your sales data.</p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onRefresh}
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#8A17D1] px-3 py-2 text-sm font-semibold text-white hover:bg-[#D326AD] disabled:cursor-not-allowed disabled:opacity-60"
                >
                    <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Refresh insights
                </button>
            </div>

            <div className="mt-5 rounded-lg border border-[#C7D2FE]/15 bg-[#071026]/60 p-4">
                {loading ? (
                    <div className="flex items-center gap-3 text-sm text-[#C7D2FE]">
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#C7D2FE]/30 border-t-[#8A17D1]" />
                        Preparing your inventory insights...
                    </div>
                ) : urgentRestocks.length > 0 ? (
                    <>
                    {summary && <p className="mb-3 text-sm leading-6 text-[#E6EEF3]">{summary}</p>}
                    <ul className="space-y-3">
                        {urgentRestocks.slice(0, 3).map((item, index) => (
                            <li key={item.productId || item.productName || index} className="flex flex-col gap-1 border-b border-[#C7D2FE]/10 pb-3 last:border-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
                                <span className="font-medium text-[#E6EEF3]">{item.productName || 'Product needs attention'}</span>
                                <span className="text-sm text-[#f3a1dc]">Restock {item.suggestedReorderQuantity || item.reorderQuantity || 'soon'} · {item.daysUntilStockout ?? 'Low'} days remaining</span>
                            </li>
                        ))}
                    </ul>
                    </>
                ) : (
                    <p className="text-sm leading-6 text-[#C7D2FE]">No urgent restock recommendations right now. Stokraze AI will show products here when stock is running low or selling unusually fast.</p>
                )}
            </div>
        </section>
    );
}

function PremiumAiTools({ tools, loading, onLoad }) {
    const cards = [
        { key: 'weekly', title: 'Weekly Sales & Profit', description: 'Review revenue trends and top sellers.' },
        { key: 'alerts', title: 'AI Stock Alerts', description: 'Find low-stock and slow-moving products.' },
        { key: 'topStock', title: 'Top-Selling Stock', description: 'See the product with the highest sales this week.' },
    ];

    return (
        <section className="col-span-full grid grid-cols-1 gap-4 md:grid-cols-3">
            {cards.map((card) => {
                const data = tools[card.key];
                return (
                    <article key={card.key} className="rounded-xl border border-[#C7D2FE]/15 bg-[#111B31] p-5 shadow-lg shadow-[#020617]/30">
                        <h3 className="font-semibold text-[#E6EEF3]">{card.title}</h3>
                        <p className="mt-1 min-h-10 text-sm text-[#C7D2FE]">{card.description}</p>
                        <button onClick={() => onLoad(card.key)} disabled={loading === card.key} className="mt-4 rounded-lg bg-[#8A17D1] px-3 py-2 text-sm font-semibold text-white hover:bg-[#D326AD] disabled:opacity-60">
                            {loading === card.key ? 'Loading...' : 'View insights'}
                        </button>
                        {data && (
                            <div className="mt-4 border-t border-[#C7D2FE]/15 pt-3 text-sm text-[#E6EEF3]">
                                {card.key === 'weekly' && (() => {
                                    const currentRevenue = Number(data.revenueThisWeek || 0);
                                    const previousRevenue = Number(data.revenuePreviousWeek || 0);
                                    const change = Number(data.revenueChangePercent || 0);
                                    const trend = change > 0 ? `Sales are up ${change}% compared with the previous 7 days.` : change < 0 ? `Sales are down ${Math.abs(change)}% compared with the previous 7 days.` : 'Sales are unchanged compared with the previous 7 days.';
                                    return <>
                                        <p>This 7 days: ₦{currentRevenue.toLocaleString()}</p>
                                        <p>Previous 7 days: ₦{previousRevenue.toLocaleString()}</p>
                                        <p className="mt-2 text-[#f3a1dc]">{trend}</p>
                                        <div className="mt-3 border-t border-[#C7D2FE]/10 pt-3">
                                            <p className="mb-1 font-medium">Top sellers this week</p>
                                            {data.topSellers?.length > 0
                                                ? data.topSellers.slice(0, 3).map((item, index) => <p key={`${item.productName}-${index}`} className="text-[#C7D2FE]">{index + 1}. {item.productName} · {item.quantitySold} sold</p>)
                                                : <p className="text-[#C7D2FE]">No sales were recorded in the last 7 days, so there are no top sellers yet.</p>}
                                        </div>
                                    </>;
                                })()}
                                {card.key === 'alerts' && <>
                                    <p className="font-medium">Low-stock items ({data.lowStock?.length || 0})</p>
                                    {data.lowStock?.length > 0 ? <p className="mt-1 text-[#C7D2FE]">{data.lowStock.join(', ')}</p> : <p className="mt-1 text-[#C7D2FE]">No low-stock items.</p>}
                                    <p className="mt-3 font-medium">Dead-stock items ({data.deadStock?.length || 0})</p>
                                    {data.deadStock?.length > 0 ? <p className="mt-1 text-[#C7D2FE]">{data.deadStock.join(', ')}</p> : <p className="mt-1 text-[#C7D2FE]">No dead-stock items.</p>}
                                </>}
                                {card.key === 'topStock' && (data.topSellers?.length > 0 ? <><p className="text-lg font-semibold text-[#f3a1dc]">{data.topSellers[0].productName}</p><p className="mt-1 text-[#C7D2FE]">{data.topSellers[0].quantitySold} unit(s) sold in the last 7 days.</p></> : <p className="text-[#C7D2FE]">No sales were recorded in the last 7 days.</p>)}
                                {card.key === 'purchaseOrders' && (
                                    data.length > 0 ? (
                                        <div className="space-y-3">
                                            {data.map((draft, draftIndex) => (
                                                <div key={`${draft.vendorName}-${draftIndex}`} className="border-b border-[#C7D2FE]/10 pb-3 last:border-0 last:pb-0">
                                                    <p className="font-medium text-[#f3a1dc]">{draft.vendorName}</p>
                                                    {draft.items?.map((item, itemIndex) => (
                                                        <p key={`${item.productId}-${itemIndex}`} className="mt-1 text-[#C7D2FE]">{item.productName} · reorder {item.suggestedReorderQuantity}</p>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    ) : <p className="text-[#C7D2FE]">No purchase-order drafts are needed right now.</p>
                                )}
                            </div>
                        )}
                    </article>
                );
            })}
        </section>
    );
}

function AiPerformanceShortcut() {
    return (
        <section className="col-span-full rounded-xl border border-[#D326AD]/35 bg-[#111B31] p-5 shadow-lg shadow-[#020617]/30">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold text-[#E6EEF3]">AI Business Performance</h2>
                        <span className="rounded-full bg-[#D326AD]/20 px-2 py-0.5 text-xs font-semibold text-[#f3a1dc]">Premium</span>
                    </div>
                    <p className="mt-1 text-sm text-[#C7D2FE]">View health score, action checklist, forecast, product performance, and AI reports.</p>
                </div>
                <Link to="ai-performance" className="inline-flex justify-center rounded-lg bg-[#8A17D1] px-4 py-2 text-sm font-semibold text-white hover:bg-[#D326AD]">
                    Open AI Performance
                </Link>
            </div>
        </section>
    );
}

function Dashboard() {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isPremium, setIsPremium] = useState(false);
    const [aiInsights, setAiInsights] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiTools, setAiTools] = useState({});
    const [aiToolLoading, setAiToolLoading] = useState(null);
    const [subscriptionInfo, setSubscriptionInfo] = useState(null);

    const authContext = useContext(AuthContext);

    const [chart, setChart] = useState({
        options: {
            chart: {
                id: "inventory-bar",
                toolbar: { show: false },
                foreColor: chartText,
            },
            colors: ["#8A17D1"],
            plotOptions: {
                bar: { borderRadius: 5, columnWidth: "55%" },
            },
            dataLabels: { enabled: false },
            grid: { borderColor: chartGrid },
            xaxis: {
                categories: [
                    "Jan","Feb","Mar","Apr","May","Jun",
                    "Jul","Aug","Sep","Oct","Nov","Dec"
                ],
                labels: { style: { colors: chartText } },
                axisBorder: { color: chartGrid },
                axisTicks: { color: chartGrid },
            },
            yaxis: { labels: { style: { colors: chartText } } },
            tooltip: { theme: "dark" },
        },
        series: [{ name: "Monthly Sales", data: Array(12).fill(0) }]
    });

    useEffect(() => {
        const loadDashboard = async () => {
            setLoading(true);

            try {
                const res = await apiFetch("/api/dashboard/summary");

                if (!res.ok) {
                    setLoading(false);
                    return;
                }

                const response = await res.json();
                const data = response.data;

                setSummary(data);

                setChart(prev => ({
                    ...prev,
                    series: [
                        {
                            name: "Monthly Sales Amount",
                            data: data.monthlySales || Array(12).fill(0)
                        }
                    ]
                }));

            } catch (err) {
                console.error(err);
            }

            setLoading(false);
        };

        loadDashboard();
    }, [authContext.user]);

    const loadAiInsights = async () => {
        setAiLoading(true);
        try {
            const res = await apiFetch('/api/ai/restock-insights');
            if (res.ok) setAiInsights(await res.json());
            else setAiInsights(null);
        } catch (err) {
            console.error('Unable to load AI insights', err);
            setAiInsights(null);
        } finally {
            setAiLoading(false);
        }
    };

    const loadAiTool = async (tool) => {
        const paths = { weekly: '/api/ai/weekly-insights', alerts: '/api/ai/stock-alerts', topStock: '/api/ai/weekly-insights' };
        setAiToolLoading(tool);
        try {
            const res = await apiFetch(paths[tool]);
            if (res.ok) {
                const response = await res.json();
                setAiTools((current) => ({ ...current, [tool]: response.data || response }));
            }
        } catch (err) {
            console.error(`Unable to load ${tool} AI tool`, err);
        } finally {
            setAiToolLoading(null);
        }
    };

    useEffect(() => {
        let mounted = true;

        const loadPremiumFeatures = async () => {
            try {
                const res = await apiFetch('/api/subscription');
                if (!res.ok) {
                    if (mounted) {
                        setSubscriptionInfo(null);
                        setIsPremium(false);
                    }
                    return;
                }
                const data = await res.json();
                const plan = data?.plan || data?.data?.plan;
                if (mounted) setSubscriptionInfo(plan || null);
                const expiry = plan?.expiresAt || plan?.ExpiresAt;
                const hasActiveExpiry = !expiry || new Date(expiry) > new Date();
                const premium = String(plan?.name || plan?.Name || '').toLowerCase().includes('premium') && hasActiveExpiry;
                if (mounted) setIsPremium(premium);
                if (premium) await loadAiInsights();
            } catch (err) {
                console.error('Unable to load subscription plan', err);
            }
        };

        if (authContext.user) loadPremiumFeatures();
        return () => { mounted = false; };
    }, [authContext.user]);

    const formatCurrency = (v) => {
        const n = Number(v) || 0;
        return `₦${n.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };
    useEffect(() => {
    console.log("Dashboard mounted");
}, []);

    const categoryData = summary?.categoryData
        ? {
            ...summary.categoryData,
            datasets: (summary.categoryData.datasets || []).map((dataset) => ({
                ...dataset,
                backgroundColor: chartPalette,
                borderColor: "#0B1220",
                borderWidth: 3,
            })),
        }
        : null;

    const doughnutOptions = {
        plugins: {
            legend: {
                labels: { color: chartText, padding: 16 },
            },
            tooltip: {
                backgroundColor: "#071026",
                titleColor: "#FFFFFF",
                bodyColor: chartText,
            },
        },
    };

    return (
        <div className="grid grid-cols-1 col-span-12 lg:col-span-10 gap-6 md:grid-cols-2 lg:grid-cols-4 p-4">

            <article className="flex flex-col gap-4 rounded-lg border bg-white p-6">
                <strong>Total Sales</strong>
                <span className="text-2xl">
                    {summary ? formatCurrency(summary.totalSales) : <Spinner />}
                </span>
            </article>

            <article className="flex flex-col gap-4 rounded-lg border bg-white p-6">
                <strong>Daily Sales</strong>
                <span className="text-2xl">
                    {summary ? formatCurrency(summary.dailySales) : <Spinner />}
                </span>
            </article>

            <article className="flex flex-col gap-4 rounded-lg border bg-white p-6">
                <strong>Returns</strong>
                <span className="text-2xl">
                    {summary ? summary.returnedCount : <Spinner />}
                </span>
            </article>

            <article className="flex flex-col gap-4 rounded-lg border bg-white p-6">
                <strong>Profit</strong>
                <span className="text-2xl">
                    {summary ? formatCurrency(summary.profit) : <Spinner />}
                </span>
            </article>

            {isPremium && <AiInventoryAdvisor insights={aiInsights} loading={aiLoading} onRefresh={loadAiInsights} />}
            <StokrazeAiAssistant isPremium={isPremium} />
            {isPremium && <PremiumAiTools tools={aiTools} loading={aiToolLoading} onLoad={loadAiTool} />}
            {isPremium && <AiPerformanceShortcut />}
            <SubscriptionReminder subscription={subscriptionInfo} />

            <div className="col-span-full flex flex-col md:flex-row bg-white rounded-lg p-6">

                <div className="w-full md:w-1/2">
                    {summary ? (
                        <Chart options={chart.options} series={chart.series} type="bar" />
                    ) : (
                        <Spinner />
                    )}
                </div>

                <div className="w-full md:w-1/2 flex items-center justify-center">
                    {summary ? (
                        <div style={{ width: "320px", height: "320px" }}>
                            <Doughnut data={categoryData} options={doughnutOptions} />
                        </div>
                    ) : (
                        <Spinner />
                    )}
                </div>

            </div>
        </div>
    );
}

export default Dashboard;
